// src/pages/MyTests.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../api";
import { useToast } from "../context/ToastContext";

export default function MyTests() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);
  

  const [selectedTestId, setSelectedTestId] = useState(null);
  const [selectedTestType, setSelectedTestType] = useState(null);

  // Get User ID safely
  let userIdRaw = null;
  try {
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      const parsed = JSON.parse(rawUser);
      userIdRaw = parsed?._id || parsed?.id || null;
    }
    if (!userIdRaw) {
      userIdRaw =
        localStorage.getItem("userId") ||
        localStorage.getItem("_id") ||
        localStorage.getItem("id") ||
        null;
    }
  } catch {
    userIdRaw = localStorage.getItem("userId") || null;
  }

  const normalizedUserId = userIdRaw ? userIdRaw.toString().trim() : null;
  const token = localStorage.getItem("token");

  // Extract creator ID
  const getCreatorId = (createdBy) => {
    if (!createdBy) return null;
    if (typeof createdBy === "string") return createdBy;
    if (createdBy._id) return createdBy._id;
    if (createdBy.id) return createdBy.id;

    try {
      const match = JSON.stringify(createdBy).match(/"(_id|id)"\s*:\s*"([^"]+)"/);
      if (match && match[2]) return match[2];
    } catch {}
    return null;
  };

  // Fetch tests
  useEffect(() => {
    let mounted = true;

    const fetchTests = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const endpoint = token ? "/publictests/all" : "/publictests/public";

        const res = await API.get(endpoint, { headers });
        if (!mounted) return;

        if (Array.isArray(res.data)) setTests(res.data);
        else if (res.data && Array.isArray(res.data.tests))
          setTests(res.data.tests);
        else setTests([]);
      } catch (err) {
        if (mounted) setError("Failed to fetch tests from server");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchTests();
    return () => {
      mounted = false;
    };
  }, [token]);

  // Confirm delete
  const confirmDelete = async () => {
    try {
      await API.delete(`/publictests/test/${testToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTests((prev) =>
        prev.filter((t) => t._id !== testToDelete && t.id !== testToDelete)
      );

      showToast("🗑️ Test deleted successfully!");
    } catch (err) {
      showToast("Failed to delete test. Try again.");
    }

    setShowDeleteModal(false);
    setTestToDelete(null);
  };

  // Edit test
  const handleEdit = (id) => navigate(`/edit-test/${id}`);

  // UI
  if (loading) return <p className="text-center mt-10">⏳ Loading tests...</p>;
  if (error) return <p className="text-center mt-10 text-red-500">{error}</p>;

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-6 text-center text-blue-700">
        🧠 My Created Tests (Public + Private)
      </h1>

      {tests.length === 0 ? (
        <p className="text-center text-gray-600">No tests found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {tests.map((test) => {
            const creatorId = getCreatorId(test.createdBy);
            const isOwner =
              normalizedUserId &&
              creatorId &&
              normalizedUserId === creatorId.toString().trim();

            return (
              <div
                key={test._id || test.id}
                className="p-6 bg-white shadow-md rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-150"
              >
                <h2 className="text-xl font-semibold mb-1 text-gray-900">
                  {test.title || "Untitled Test"}
                </h2>
                <p className="text-gray-600 mb-2">
                  {test.description || "No description"}
                </p>
                <p className="text-gray-500 mb-3 text-sm">
                  🧾 Questions:{" "}
                  {Array.isArray(test.questions)
                    ? test.questions.length
                    : test.questions || 0}
                </p>

                <p
                  className={`text-sm font-semibold mb-4 ${
                    test.visibility === "public"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {test.visibility === "public"
                    ? "🌍 Public Test"
                    : "🔒 Private Test"}
                </p>

                <button
  onClick={() => {
    setSelectedTestId(test._id || test.id);
    setSelectedTestType(test.type);
  }}
  className="block w-full text-center bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
>
  Attempt Test
</button>


                {token && isOwner && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleEdit(test._id || test.id)}
                      className="flex-1 bg-yellow-400 text-black font-semibold py-2 rounded-lg hover:bg-yellow-500 border border-black transition"
                    >
                      ✏️ Edit
                    </button>

                    <button
                      onClick={() => {
                        setTestToDelete(test._id || test.id);
                        setShowDeleteModal(true);
                      }}
                      className="flex-1 bg-red-600 text-white font-semibold py-2 rounded-lg hover:bg-red-700 transition"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-80 p-6 rounded-xl shadow-lg text-center">
            <h2 className="text-xl font-semibold text-red-600 mb-2">
              ⚠️ Delete Test?
            </h2>

            <p className="text-gray-700 mb-5">
              Are you sure you want to delete this test?
              <br />
              This action cannot be undone.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-gray-300 py-2 rounded-lg hover:bg-gray-400 transition"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {selectedTestId && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-xl w-80 shadow-lg">

      <h2 className="text-xl font-bold text-center mb-4">
        Select Test Mode
      </h2>

<button
        onClick={() => navigate(`/test/${selectedTestType}/${selectedTestId}`)}
        className="w-full mb-2 bg-blue-600 text-white py-2 rounded"
      >
        📝 Normal Mode
      </button>


      <button
        onClick={() => navigate(`/practice/${selectedTestId}`)}
        className="w-full mb-2 bg-green-600 text-white py-2 rounded"
      >
        🧠 Practice Mode
      </button>

      

      <button
        onClick={() => navigate(`/timed/${selectedTestId}`)}
        className="w-full mb-2 bg-red-600 text-white py-2 rounded"
      >
        ⏱ Timed Mode
      </button>

      <button
        onClick={() => setSelectedTestId(null)}
        className="mt-3 w-full border py-2 rounded"
      >
        Cancel
      </button>

    </div>
  </div>
)}

    </div>
  );
}
